import React, { useRef, useState } from 'react';
import { LayoutDashboard, ClipboardList, Plus, PiggyBank, Settings as SettingsIcon } from 'lucide-react';
import { useBudget } from '../../context/BudgetContext';
import useSlidingPill from '../../hooks/useSlidingPill';

const PRIMARY = [
  { id: 'dashboard', label: 'Dashboard', Icon: LayoutDashboard },
  { id: 'tracker',   label: 'Tracker',   Icon: ClipboardList },
];
const PRIMARY_RIGHT = [
  { id: 'savings',   label: 'Savings',   Icon: PiggyBank },
  { id: 'settings',  label: 'Settings',  Icon: SettingsIcon },
];

export default function BottomNav({ tab, onTab, onAdd, compact }) {
  const {
    googleDriveOn, isDriveSyncing, isDriveConnecting,
    driveNeedsReconnect, driveSyncError, driveDirty,
  } = useBudget();

  // The FAB doubles as a passive Drive-sync indicator: the background turns red
  // when there are unsynced local changes or sync is broken, and the + icon
  // spins while syncing — so a sync-in-progress shows a spinning + on a red
  // background, staying red until the sync succeeds. White means synced (or
  // Drive off). Tap action is unchanged.
  const fabSyncing = googleDriveOn && (isDriveSyncing || isDriveConnecting);
  const fabProblem = googleDriveOn && (driveNeedsReconnect || driveSyncError || driveDirty);

  // The whole bar is swipable: swipe anywhere across it to scrub the glass
  // pill, which snaps to the nearest tab on release. A plain tap is untouched —
  // we only capture the pointer once a real horizontal drag begins, so button
  // taps still fire their click.
  const [navRef, pill] = useSlidingPill('.bn-item.active', `${tab}|${compact}`);
  const [dragX, setDragX] = useState(null);
  const drag = useRef(null);
  const suppressClick = useRef(false);

  const pillLeft = dragX ?? pill?.left ?? 0;
  const items = () => [...(navRef.current?.querySelectorAll('.bn-item') || [])];

  const onPointerDown = (e) => {
    if (!pill) return;
    const its = items();
    if (!its.length) return;
    drag.current = {
      id: e.pointerId,
      startX: e.clientX,
      startLeft: pill.left,
      curLeft: pill.left,
      min: its[0].offsetLeft,
      max: its[its.length - 1].offsetLeft,
      moved: false,
    };
  };
  const onPointerMove = (e) => {
    const d = drag.current;
    if (!d) return;
    const dx = e.clientX - d.startX;
    if (!d.moved && Math.abs(dx) > 5) {
      d.moved = true;
      navRef.current?.setPointerCapture(d.id); // now own the gesture until release
    }
    if (d.moved) {
      d.curLeft = Math.max(d.min, Math.min(d.max, d.startLeft + dx));
      setDragX(d.curLeft);
    }
  };
  const onPointerUp = () => {
    const d = drag.current;
    drag.current = null;
    if (d?.moved) {
      suppressClick.current = true; // swallow the click the drag would synthesize
      const center = d.curLeft + pill.width / 2;
      const nearest = items().reduce((best, it) => {
        const dist = Math.abs(it.offsetLeft + it.offsetWidth / 2 - center);
        return dist < best.dist ? { id: it.dataset.id, dist } : best;
      }, { id: null, dist: Infinity });
      if (nearest.id) onTab(nearest.id);
    }
    setDragX(null);
  };
  const onTabClick = (id) => {
    if (suppressClick.current) { suppressClick.current = false; return; }
    onTab(id);
  };

  return (
    <nav
      className={`bottom-nav${compact ? ' compact' : ''}`}
      aria-label="Primary"
      ref={navRef}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={onPointerUp}
      onPointerCancel={onPointerUp}
    >
      {pill && (
        <span
          className={`bn-pill${dragX !== null ? ' dragging' : ''}`}
          style={{ transform: `translateX(${pillLeft - 2}px)`, width: pill.width + 4 }}
        />
      )}
      {PRIMARY.map(({ id, label, Icon }) => (
        <button key={id} data-id={id} className={`bn-item${tab === id ? ' active' : ''}`} onClick={() => onTabClick(id)}>
          <Icon size={20} />
          <span>{label}</span>
        </button>
      ))}

      <button
        className={`bn-fab${fabProblem ? ' drive-problem' : ''}`}
        onClick={() => { if (suppressClick.current) { suppressClick.current = false; return; } onAdd(); }}
        aria-label="Add transaction"
        title={fabSyncing ? 'Syncing to Drive…'
             : driveNeedsReconnect ? 'Drive needs reconnect'
             : fabProblem ? 'Unsynced changes' : 'Add transaction'}
      >
        <Plus size={24} strokeWidth={2.5} className={fabSyncing ? 'spin' : undefined} />
      </button>

      {PRIMARY_RIGHT.map(({ id, label, Icon }) => (
        <button key={id} data-id={id} className={`bn-item${tab === id ? ' active' : ''}`} onClick={() => onTabClick(id)}>
          <Icon size={20} />
          <span>{label}</span>
        </button>
      ))}
    </nav>
  );
}
